import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { Domain, EPinpointRequestStatus } from '@feathr/blackbox';
import { domainStatusToColorMap, domainStatusToLabelMap } from '@feathr/blackbox';
import { Button, EMouseLeaveDelay, Pill } from '@feathr/components';
import { useRefreshDomain } from '@feathr/extender/hooks';
import { useFlags } from '@feathr/extender/state';

interface IProps {
  domain: Domain;
}

function EmailStatusPill({ domain }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const flags = useFlags();
  const refreshDomain = useRefreshDomain(domain);
  const status = domain.emailSendingStatus(flags.bypassMX);

  function handleClickRefresh(): void {
    void refreshDomain();
  }

  const tooltipMap: Partial<Record<EPinpointRequestStatus, ReactNode>> = {
    Success: t('This domain, and any sender from it, are ready to be used in email campaigns.'),
    Failed: (
      <Trans t={t}>
        Add CNAME and mail records for this domain to your DNS host and then{' '}
        <Button onClick={handleClickRefresh} type={'link'}>
          refresh it
        </Button>
        .
      </Trans>
    ),
    NotStarted: t("This domain isn't setup to be used for email."),
  };

  return (
    <Pill
      isLoading={domain.isPending}
      mouseLeaveDelay={EMouseLeaveDelay.Link}
      theme={domainStatusToColorMap[status]}
      tooltip={tooltipMap[status]}
    >
      {domainStatusToLabelMap[status]}
    </Pill>
  );
}

export default observer(EmailStatusPill);
